import { Nullable } from '@/utils/types';
import { ITextValue } from '@/types/common';
import { getTotalCarParks } from '@/bundles/Property/helpers/parks/getTotalCarParks';
import { formatCustomFieldList } from '@/bundles/BaseTable/helpers/customFieldFormatters';
import { getBuildingSizeLabel } from '@/bundles/MeasurementUnits/helpers/format/getBuildingSizeFormatted';
import { getBuildingDimensionsLabel } from '@/bundles/MeasurementUnits/helpers/format/getBuildingDimensionLabel';
import { getFloorOfFloorsLabel } from '@/bundles/Availabilities/helpers/getFloorOfFloorsLabel';
import { getAvailabilityParkingRatio } from '@/bundles/Availabilities/helpers/getAvailabilityParkingRatio';
import { AvailabilityCustomFieldsEnum, IAvailabilityCustomFieldRecord } from '@/bundles/Availabilities/types';

// Each value mirrors the label the availability details page uses for the same field.

export const getCustomFieldValue = (
  availability: IAvailabilityCustomFieldRecord,
  customField: Nullable<AvailabilityCustomFieldsEnum>
): Nullable<string | number> => {
  try {
    switch (customField) {
      case AvailabilityCustomFieldsEnum.max_contiguous_space:
        return getBuildingSizeLabel(availability.max_contiguous_space);
      case AvailabilityCustomFieldsEnum.floor:
        return getFloorOfFloorsLabel(availability.floor, availability.details.building.floor_count);
      case AvailabilityCustomFieldsEnum.use_types:
        return formatCustomFieldList(availability.use_types.map((useType) => useType.text));
      case AvailabilityCustomFieldsEnum.primary_use:
        return availability.primary_use;
      case AvailabilityCustomFieldsEnum.building_class:
        return availability.details.building.class;
      case AvailabilityCustomFieldsEnum.rentable_building_size:
        return getBuildingSizeLabel(availability.property.size.building.rentable);
      case AvailabilityCustomFieldsEnum.column_spacing:
        return getBuildingDimensionsLabel(
          availability.details.building.column.width,
          availability.details.building.column.depth,
          'x'
        );
      // getTotalCarParks returns 0 when there are no spaces; null lets the column show N/A.
      case AvailabilityCustomFieldsEnum.car_parks:
        return getTotalCarParks(availability.details.parking.spaces.covered, availability.details.parking.spaces.uncovered) || null;
      case AvailabilityCustomFieldsEnum.parking_ratio:
        return getAvailabilityParkingRatio({
          ratio: availability.details.parking.ratio,
          covered: availability.details.parking.spaces.covered,
          uncovered: availability.details.parking.spaces.uncovered,
          buildingRentable: availability.property.size.building.rentable,
        });
      case AvailabilityCustomFieldsEnum.trailer_spots:
        return availability.details.loading_doors.trailer_spots;
      case AvailabilityCustomFieldsEnum.zoning:
        return availability.details.site.zoning;
      case AvailabilityCustomFieldsEnum.county: {
        const county = availability.address.county;

        return typeof county === 'object' && county !== null ? county.name : county;
      }
      default:
        return null;
    }
  } catch (error) {
    return null;
  }
};

export const getAvailabilityCustomFieldItems = (): ITextValue<AvailabilityCustomFieldsEnum>[] => [
  { text: 'Max Contiguous Space', value: AvailabilityCustomFieldsEnum.max_contiguous_space },
  { text: 'Floor of # Floors', value: AvailabilityCustomFieldsEnum.floor },
  { text: 'Use Types', value: AvailabilityCustomFieldsEnum.use_types },
  { text: 'Primary Use', value: AvailabilityCustomFieldsEnum.primary_use },
  { text: 'Building Class', value: AvailabilityCustomFieldsEnum.building_class },
  { text: 'Rentable Building Size', value: AvailabilityCustomFieldsEnum.rentable_building_size },
  { text: 'Column Spacing (W x D)', value: AvailabilityCustomFieldsEnum.column_spacing },
  { text: 'Car Parks', value: AvailabilityCustomFieldsEnum.car_parks },
  { text: 'Parking Ratio', value: AvailabilityCustomFieldsEnum.parking_ratio },
  { text: 'Trailer Spots', value: AvailabilityCustomFieldsEnum.trailer_spots },
  { text: 'Zoning', value: AvailabilityCustomFieldsEnum.zoning },
  { text: 'County', value: AvailabilityCustomFieldsEnum.county },
];
